import { useTranslation } from 'react-i18next';
import { SchedulerSidebarResourcesRequirementsProps } from './types';
import { Button } from '@components/ui/button';
import { useEffect, useRef, useState } from 'react';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { requirementsFillColor } from './helpers';
import { cn } from '@utils/cn';
import { MAX_TOTAL_NEEDED } from './constants';

export const SchedulerSidebarResourcesRequirements = ({
  requirement,
  onChangeAmount,
  scheduleStatus,
}: SchedulerSidebarResourcesRequirementsProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'schedule.requirements' });

  const [totalNeeded, setTotalNeeded] = useState(requirement.totalNeeded || 0);
  const [totalNeededInput, setTotalNeededInput] = useState(String(requirement.totalNeeded || 0));
  const editingRef = useRef(false);
  const revertRef = useRef(false);
  const { currentlyAssigned } = requirement;

  const handleChangeTotalNeeded = (value: number) => {
    setTotalNeeded(value);
    setTotalNeededInput(String(value));
    onChangeAmount(value);
  };

  const handleInputChange = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (Number(digits) > MAX_TOTAL_NEEDED) return;

    setTotalNeededInput(digits);
  };

  const commitInput = () => {
    editingRef.current = false;

    if (revertRef.current || !totalNeededInput) {
      revertRef.current = false;
      setTotalNeededInput(String(totalNeeded));
      return;
    }

    handleChangeTotalNeeded(Math.min(Number(totalNeededInput), MAX_TOTAL_NEEDED));
  };

  useEffect(() => {
    if (editingRef.current) return;

    setTotalNeeded(requirement.totalNeeded);
    setTotalNeededInput(String(requirement.totalNeeded ?? 0));
  }, [requirement.totalNeeded]);

  return (
    <div className="flex flex-row flex-wrap items-center justify-between gap-2">
      <strong className="flex h-7 items-center text-xs font-semibold">
        {t(`${requirement.requirementType}.title`)}
      </strong>
      <div
        className={cn([
          'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 gap-1',
          requirementsFillColor('border', totalNeeded, currentlyAssigned || 0, scheduleStatus),
          requirementsFillColor('bg', totalNeeded, currentlyAssigned || 0, scheduleStatus),
        ])}>
        <span
          className={`text-xs font-bold leading-none ${requirementsFillColor('text', totalNeeded, currentlyAssigned || 0, scheduleStatus)}`}>
          {t('assigned', { count: currentlyAssigned || 0 })}
        </span>
        <span
          className={`text-xs ${requirementsFillColor('text', totalNeeded, currentlyAssigned || 0, scheduleStatus)}`}>
          · {t('need')}
        </span>
        <input
          type="text"
          name={`totalNeeded-${requirement.id}`}
          inputMode="numeric"
          maxLength={String(MAX_TOTAL_NEEDED).length}
          data-revert-on-escape
          value={totalNeededInput}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            editingRef.current = true;
            if (totalNeededInput === '0') {
              setTotalNeededInput('');
            }
          }}
          onBlur={commitInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.blur();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              revertRef.current = true;
              e.currentTarget.blur();
            }
          }}
          className={cn([
            'h-4 w-7 rounded border border-neutral-300 bg-white px-0.5 py-0 text-center text-xs font-bold leading-none shadow-sm focus:outline-none focus:ring-1 focus:ring-neutral-950',
            requirementsFillColor('text', totalNeeded, currentlyAssigned || 0, scheduleStatus),
          ])}
        />
        <Button
          variant="transparent"
          size="xs"
          className={requirementsFillColor(
            'bg',
            totalNeeded,
            currentlyAssigned || 0,
            scheduleStatus,
          )}
          disabled={totalNeeded === 0}
          onClick={(e) => {
            e.stopPropagation();
            const newTotalNeeded = totalNeeded - 1;
            if (newTotalNeeded < 0) return;
            handleChangeTotalNeeded(newTotalNeeded);
          }}>
          <SvgConsumer
            id="minus"
            className={requirementsFillColor(
              'text',
              totalNeeded,
              currentlyAssigned || 0,
              scheduleStatus,
            )}
          />
        </Button>
        <Button
          variant="transparent"
          size="xs"
          disabled={totalNeeded >= MAX_TOTAL_NEEDED}
          onClick={(e) => {
            e.stopPropagation();
            handleChangeTotalNeeded(totalNeeded + 1);
          }}>
          <SvgConsumer
            id="plus"
            className={requirementsFillColor(
              'text',
              totalNeeded,
              currentlyAssigned || 0,
              scheduleStatus,
            )}
          />
        </Button>
      </div>
    </div>
  );
};
